import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, delay, map, mergeMap, of, retry, tap } from 'rxjs';

import { Language } from '@/organisations/models';
import {
    NestedQuestion,
    Question,
    Questionnaire,
    QuestionnaireSubmission,
    QuestionnaireSync,
    TaxQuestion,
    TaxResponse,
    TaxResponseDocument,
    TaxRole,
} from '@/tax-detail/models';
import { QuestionnaireTemplate } from '@/tax-detail/models/questionnaire-template.model';
import { PersonalTaxSettings, PollingStatus, TemplateMode } from '@/tax/models';

import { AppService } from './app.service';
import { StringInterpolationService } from './string-interpolation.service';

import { environment } from '@env';

const DELAY_STATUS_FETCH_MS = 1000;
const invalidErrorMessage = 'Not ready';

@Injectable({
    providedIn: 'root',
})
export class PersonalTaxQuestionnaireService {
    constructor(private http: HttpClient, private interpolator: StringInterpolationService, private appService: AppService) {}

    private getUserLanguage(): Language {
        return this.appService.getCurrentLocale() as Language;
    }

    private generateQuestion(data: any, interpolationData?: Record<string, string>): Question {
        const question = new Question().deserialize(data);
        question.titles.forEach((titleItem) => {
            titleItem.title = this.interpolator.interpolateString(titleItem.title, interpolationData);
            titleItem.subtitle =
                titleItem.subtitle !== undefined ? this.interpolator.interpolateString(titleItem.subtitle, interpolationData) : undefined;
        });
        return question;
    }

    private interPolateNestedQuestion(question: NestedQuestion, interpolationData?: Record<string, string>) {
        question.titles.forEach((titleItem) => {
            titleItem.title = this.interpolator.interpolateString(titleItem.title, interpolationData);
            titleItem.subtitle =
                titleItem.subtitle !== undefined ? this.interpolator.interpolateString(titleItem.subtitle, interpolationData) : undefined;
        });
        question.inputFields?.forEach((field) => {
            field.options?.forEach((option) => {
                option.text = option.text.map((textStr) => this.interpolator.interpolateString(textStr, interpolationData));
            });
            field.labels.forEach((label) => {
                label.text = this.interpolator.interpolateString(label.text, interpolationData);
            });
            if (field.nestedQuestion !== undefined) {
                this.interPolateNestedQuestion(field.nestedQuestion, interpolationData);
            }
        });
    }

    private generateTaxQuestion(data: any, interpolationData?: Record<string, string>): TaxQuestion {
        const question = new TaxQuestion().deserialize(data);
        if (question.question !== undefined) {
            this.interPolateNestedQuestion(question.question, interpolationData);
        }
        return question;
    }

    createQuestionnaire(orgId: number, taxId: number, templateMode: TemplateMode, template?: number): Observable<Questionnaire> {
        return this.http
            .post<any>(environment.endpoints.taxQuestionnaire(orgId, taxId), { 'template_mode': templateMode, template: template })
            .pipe(map<any, Questionnaire>((questionnaire) => new Questionnaire().deserialize(questionnaire)));
    }

    getQuestionnaire(orgId: number, taxId: number): Observable<Questionnaire> {
        return this.http
            .get<any>(environment.endpoints.taxQuestionnaire(orgId, taxId))
            .pipe(map<any, Questionnaire>((questionnaire) => new Questionnaire().deserialize(questionnaire)));
    }

    updateQuestionnaire(orgId: number, taxId: number, questionnaire: Questionnaire): Observable<Questionnaire> {
        return this.http
            .put<Questionnaire>(environment.endpoints.taxQuestionnaire(orgId, taxId), questionnaire.serialize())
            .pipe(map<any, Questionnaire>((questionnaire) => new Questionnaire().deserialize(questionnaire)));
    }

    sendQuestionnaire(orgId: number, taxId: number, role?: TaxRole): Observable<Questionnaire> {
        return this.http
            .post<any>(environment.endpoints.taxQuestionnaireSend(orgId, taxId), { role })
            .pipe(map<any, Questionnaire>((questionnaire) => new Questionnaire().deserialize(questionnaire)));
    }

    getAllCompanyTaxQuestions(orgId: number, companyId: number, interpolationData?: Record<string, string>): Observable<Question[]> {
        return this.http.get<any>(environment.endpoints.taxQuestionsList(orgId, companyId)).pipe(
            map<any, Question[]>((questions) =>
                questions.map((question: any) => {
                    const ques = this.generateQuestion(question, interpolationData);
                    ques.localize(this.getUserLanguage());
                    return ques;
                }),
            ),
        );
    }

    getAllOrganisationTaxQuestions(orgId: number): Observable<Question[]> {
        return this.http.get<any>(environment.endpoints.taxQuestions(orgId)).pipe(
            map<any, Question[]>((questions) =>
                questions
                    .map((question: any) => {
                        const ques = this.generateQuestion(question);
                        ques.localize(this.getUserLanguage());
                        return ques;
                    })
                    .sort(Question.Sort),
            ),
        );
    }

    getAllTaxQuestionnaireTemplates(orgId: number): Observable<QuestionnaireTemplate[]> {
        return this.http
            .get<any>(environment.endpoints.taxQuestionnaireTemplates(orgId))
            .pipe(
                map<any, QuestionnaireTemplate[]>((templates) =>
                    templates.map((template: any) => new QuestionnaireTemplate().deserialize(template)),
                ),
            );
    }

    getPersonalTaxSettings(orgId: number): Observable<PersonalTaxSettings> {
        return this.http
            .get<any>(environment.endpoints.taxSettings(orgId))
            .pipe(map<any, PersonalTaxSettings>((personalTaxSettings) => new PersonalTaxSettings().deserialize(personalTaxSettings)));
    }

    updatePersonalTaxSettings(orgId: number, settings: PersonalTaxSettings): Observable<PersonalTaxSettings> {
        return this.http
            .put<PersonalTaxSettings>(environment.endpoints.taxSettings(orgId), settings.serialize())
            .pipe(map((personalTaxSettings) => new PersonalTaxSettings().deserialize(personalTaxSettings)));
    }

    createPersonalTaxTemplate(orgId: number, template: QuestionnaireTemplate): Observable<QuestionnaireTemplate> {
        return this.http
            .post<QuestionnaireTemplate>(environment.endpoints.taxQuestionnaireTemplates(orgId), template.serialize())
            .pipe(map((template) => new QuestionnaireTemplate().deserialize(template)));
    }

    updatePersonalTaxTemplate(orgId: number, template: QuestionnaireTemplate): Observable<QuestionnaireTemplate> {
        return this.http
            .put<QuestionnaireTemplate>(environment.endpoints.taxQuestionnaireTemplatesDetails(orgId, template.id), template.serialize())
            .pipe(map((template) => new QuestionnaireTemplate().deserialize(template)));
    }

    deletePersonalTaxTemplate(orgId: number, templateId: number): Observable<any> {
        return this.http.delete(environment.endpoints.taxQuestionnaireTemplatesDetails(orgId, templateId));
    }

    createCompanyTaxQuestion(
        orgId: number,
        companyId: number,
        question: Question,
        interpolationData?: Record<string, string>,
    ): Observable<Question> {
        return this.http.post<Question>(environment.endpoints.taxQuestionsList(orgId, companyId), question.serialize()).pipe(
            map((question) => {
                const ques = this.generateQuestion(question, interpolationData);
                ques.localize(this.getUserLanguage());
                return ques;
            }),
        );
    }

    updateCompanyTaxQuestion(
        orgId: number,
        companyId: number,
        question: Question,
        interpolationData?: Record<string, string>,
    ): Observable<Question> {
        return this.http.put<Question>(environment.endpoints.taxQuestionsDetails(orgId, companyId, question.id), question.serialize()).pipe(
            map((question) => {
                const ques = this.generateQuestion(question, interpolationData);
                ques.localize(this.getUserLanguage());
                return ques;
            }),
        );
    }

    removeCompanyTaxQuestion(orgId: number, companyId: number, questionId: number): Observable<any> {
        return this.http.delete(environment.endpoints.taxQuestionsDetails(orgId, companyId, questionId));
    }

    getAllTaxQuestions(orgId: number, taxId: number, interpolationData?: Record<string, string>): Observable<TaxQuestion[]> {
        return this.http.get<any>(environment.endpoints.taxQuestionnaireQuestionsList(orgId, taxId)).pipe(
            map((questions) =>
                questions
                    .map((question: any) => {
                        const ques = this.generateTaxQuestion(question, interpolationData);
                        ques.localize(this.getUserLanguage());
                        return ques;
                    })
                    .sort(TaxQuestion.Sort),
            ),
        );
    }

    changeTaxQuestionOrder(
        orgId: number,
        taxId: number,
        questionId: number,
        requestedIndex: number,
        interpolationData?: Record<string, string>,
    ): Observable<TaxQuestion[]> {
        return this.http
            .put<any>(environment.endpoints.taxQuestionnaireQuestionsListChangeOrder(orgId, taxId), {
                question: questionId,
                'requested_index': requestedIndex,
            })
            .pipe(
                map((questions) =>
                    questions
                        .map((question: any) => {
                            const ques = this.generateTaxQuestion(question, interpolationData);
                            ques.localize(this.getUserLanguage());
                            return ques;
                        })
                        .sort(TaxQuestion.Sort),
                ),
            );
    }

    createTaxQuestion(
        orgId: number,
        taxId: number,
        taxQuestion: TaxQuestion,
        interpolationData?: Record<string, string>,
    ): Observable<TaxQuestion> {
        return this.http.post<TaxQuestion>(environment.endpoints.taxQuestionnaireQuestionsList(orgId, taxId), taxQuestion.serialize()).pipe(
            map((question) => {
                const ques = this.generateTaxQuestion(question, interpolationData);
                ques.localize(this.getUserLanguage());
                return ques;
            }),
        );
    }

    updateTaxQuestion(
        orgId: number,
        taxId: number,
        taxQuestion: TaxQuestion,
        interpolationData?: Record<string, string>,
    ): Observable<TaxQuestion> {
        return this.http
            .put<TaxQuestion>(environment.endpoints.taxQuestionnaireQuestionsDetails(orgId, taxId, taxQuestion.id), taxQuestion.serialize())
            .pipe(
                map((question) => {
                    const ques = this.generateTaxQuestion(question, interpolationData);
                    ques.localize(this.getUserLanguage());
                    return ques;
                }),
            );
    }

    removeTaxQuestion(orgId: number, taxId: number, taxQuestionId: number): Observable<any> {
        return this.http.delete(environment.endpoints.taxQuestionnaireQuestionsDetails(orgId, taxId, taxQuestionId));
    }

    getAllTaxResponses(orgId: number, taxId: number): Observable<TaxResponse[]> {
        return this.http
            .get<any>(environment.endpoints.taxQuestionnaireResponsesList(orgId, taxId))
            .pipe(map((responses) => responses.map((resp: any) => new TaxResponse().deserialize(resp))));
    }

    getTaxResponse(orgId: number, taxId: number, respId: number): Observable<TaxResponse> {
        return this.http
            .get<any>(environment.endpoints.taxQuestionnaireResponsesDetails(orgId, taxId, respId))
            .pipe(map((response) => new TaxResponse().deserialize(response)));
    }

    createTaxResponse(orgId: number, taxId: number, taxResponse: TaxResponse): Observable<TaxResponse> {
        return this.http
            .post<TaxResponse>(environment.endpoints.taxQuestionnaireResponsesList(orgId, taxId), taxResponse.serialize())
            .pipe(map((resp) => new TaxResponse().deserialize(resp)));
    }

    updateTaxResponse(orgId: number, taxId: number, taxResponse: TaxResponse): Observable<TaxResponse> {
        return this.http
            .put<TaxQuestion>(environment.endpoints.taxQuestionnaireResponsesDetails(orgId, taxId, taxResponse.id), taxResponse.serialize())
            .pipe(map((resp) => new TaxResponse().deserialize(resp)));
    }

    removeTaxResponse(orgId: number, taxId: number, taxResponseId: number): Observable<any> {
        return this.http.delete(environment.endpoints.taxQuestionnaireResponsesDetails(orgId, taxId, taxResponseId));
    }

    getAllTaxResponseDocuments(orgId: number, taxId: number, respId: number): Observable<TaxResponseDocument[]> {
        return this.http
            .get<any>(environment.endpoints.taxQuestionnaireResponseDocsList(orgId, taxId, respId))
            .pipe(map((docs) => docs.map((doc: any) => new TaxResponseDocument().deserialize(doc))));
    }

    createTaxResponseDocument(
        orgId: number,
        taxId: number,
        respId: number,
        file: File,
        uuid: string,
        recId: number,
    ): Observable<TaxResponseDocument> {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('uuid', uuid);
        formData.append('response_record', recId.toString()); //FormData has to be a string, but django deals with that automagically
        return this.http
            .post(environment.endpoints.taxQuestionnaireResponseDocsList(orgId, taxId, respId), formData)
            .pipe(map((doc) => new TaxResponseDocument().deserialize(doc)));
    }

    removeTaxResponseDocument(orgId: number, taxId: number, respId: number, docId: number): Observable<any> {
        return this.http.delete(environment.endpoints.taxQuestionnaireResponseDocsDetails(orgId, taxId, respId, docId));
    }

    createQuestionnaireSyncToSilverfin(orgId: number, taxId: number): Observable<QuestionnaireSync> {
        return this.http
            .post<any>(environment.endpoints.taxQuestionnaireSyncToSilverfinList(orgId, taxId), {})
            .pipe(map((sync) => new QuestionnaireSync().deserialize(sync)));
    }

    createQuestionnaireSyncFromSilverfin(orgId: number, taxId: number): Observable<QuestionnaireSync> {
        return this.http
            .post<any>(environment.endpoints.taxQuestionnaireSyncFromSilverfinList(orgId, taxId), {})
            .pipe(map((sync) => new QuestionnaireSync().deserialize(sync)));
    }

    retrieveQuestionnaireSyncToSilverfin(orgId: number, taxId: number, syncId: number): Observable<QuestionnaireSync> {
        return this.http
            .get<any>(environment.endpoints.taxQuestionnaireSyncToSilverfinDetail(orgId, taxId, syncId))
            .pipe(map((sync) => new QuestionnaireSync().deserialize(sync)));
    }

    retrieveQuestionnaireSyncFromSilverfin(orgId: number, taxId: number, syncId: number): Observable<QuestionnaireSync> {
        return this.http
            .get<any>(environment.endpoints.taxQuestionnaireSyncFromSilverfinDetail(orgId, taxId, syncId))
            .pipe(map((sync) => new QuestionnaireSync().deserialize(sync)));
    }

    createQuestionnaireSyncToSilverfinAndPollResult(orgId: number, taxId: number): Observable<QuestionnaireSync> {
        return this.createQuestionnaireSyncToSilverfin(orgId, taxId).pipe(
            delay(DELAY_STATUS_FETCH_MS),
            mergeMap((newQuestionnaireSync) =>
                this.retrieveQuestionnaireSyncToSilverfin(orgId, taxId, newQuestionnaireSync.id).pipe(
                    tap((sync) => {
                        if (sync.status === PollingStatus.Init || sync.status === PollingStatus.InProgress) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    createQuestionnaireSyncFromSilverfinAndPollResult(orgId: number, taxId: number): Observable<QuestionnaireSync> {
        return this.createQuestionnaireSyncFromSilverfin(orgId, taxId).pipe(
            delay(DELAY_STATUS_FETCH_MS),
            mergeMap((newQuestionnaireSync) =>
                this.retrieveQuestionnaireSyncFromSilverfin(orgId, taxId, newQuestionnaireSync.id).pipe(
                    tap((sync) => {
                        if (sync.status === PollingStatus.Init || sync.status === PollingStatus.InProgress) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    submitQuestionnaire(orgId: number, taxId: number): Observable<QuestionnaireSubmission> {
        return this.http
            .post<any>(environment.endpoints.taxQuestionnaireSubmissionList(orgId, taxId), {})
            .pipe(map((submission) => new QuestionnaireSubmission().deserialize(submission)));
    }

    reOpenQuestionnaire(orgId: number, taxId: number, submissionId: number, message?: string): Observable<QuestionnaireSubmission> {
        return this.http
            .put<any>(environment.endpoints.taxQuestionnaireSubmissionDetails(orgId, taxId, submissionId), { blocking: false, message })
            .pipe(map((submission) => new QuestionnaireSubmission().deserialize(submission)));
    }
}
